/**
 * Naming the days ad spend is missing.
 *
 * The reports used to withhold a channel's whole spend figure when any day of
 * the window had no `ad_spend` row, and then rendered that withheld figure as
 * $0 — so a channel that ran ads thirteen days of fourteen looked free, and
 * dropped out of the spend total, CAC and ROAS without saying so. Spend is now
 * the sum of the days that landed, which is only honest if the reader can see
 * which days did not. That is what this formats.
 *
 * Dates arrive as `YYYY-MM-DD` day labels, already Eastern (reportingWindow) or
 * already UTC (ad_spend) — a label, not an instant. They are split by hand
 * rather than passed to `new Date()`, which would read them as UTC midnight and
 * shift them a day back for anyone west of Greenwich.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** `2026-09-20` → `Sep 20`. Returns the input unchanged if it is not a day label. */
export function formatDayLabel(day) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day ?? ''))
  if (!match) return String(day ?? '')
  const month = MONTHS[Number(match[2]) - 1]
  return month ? `${month} ${Number(match[3])}` : String(day)
}

/**
 * The same dates as a table-cell fragment: short enough not to widen a column,
 * with the full list left to the cell's `title`.
 */
export function missingDaysBrief(days, { limit = 4 } = {}) {
  const list = (days || []).filter(Boolean)
  if (!list.length) return null
  const shown = list.slice(0, limit).map(formatDayLabel)
  const rest = list.length - shown.length
  return `missing ${shown.join(', ')}${rest > 0 ? ` +${rest} more` : ''}`
}

/**
 * A sentence naming the uncovered days, or `null` when the window is complete.
 *
 * Every date is named up to `limit`, because "3 days missing" does not let
 * anyone judge whether the gap matters — a missing Sunday and a missing launch
 * day are different facts. Past the limit the tail is counted rather than
 * printed, so a long quiet range cannot push the table off the screen.
 */
export function missingDaysNote(days, { limit = 8 } = {}) {
  const list = (days || []).filter(Boolean)
  if (!list.length) return null
  const shown = list.slice(0, limit).map(formatDayLabel)
  const rest = list.length - shown.length
  const dates = rest > 0 ? `${shown.join(', ')} and ${rest} more` : shown.join(', ')
  return `No ad spend recorded for ${list.length === 1 ? 'this date' : 'these dates'}: ${dates}.`
}
